import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from './Modal';

interface UserNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userName: string) => void | Promise<void>;
  initialName?: string;
  isLoading?: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
  loadingLabel?: string;
  error?: string | null;
  inputTestId?: string;
  submitTestId?: string;
}

export function UserNameModal({
  isOpen,
  onClose,
  onSubmit,
  initialName = '',
  isLoading = false,
  title = 'Enter Your Name',
  description = 'This board requires participants to enter their name before joining.',
  submitLabel = 'Join Board',
  loadingLabel = 'Joining...',
  error = null,
  inputTestId = 'join-user-name-input',
  submitTestId = 'join-board-submit',
}: UserNameModalProps): JSX.Element {
  const [userName, setUserName] = useState(initialName);

  useEffect(() => {
    if (isOpen) {
      setUserName(initialName);
    }
  }, [initialName, isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (userName.trim() && !isLoading) {
      void onSubmit(userName.trim());
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">
          {description}
        </p>

        <div>
          <label
            htmlFor="userName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Your Name
          </label>
          <input
            type="text"
            id="userName"
            data-testid={inputTestId}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Enter your name"
            autoFocus
            disabled={isLoading}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        {error && (
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={submitTestId}
            disabled={!userName.trim() || isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? loadingLabel : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
